import ActionButton from "@/components/buttons/ActionButton";
import DeleteIcon from "@/components/icons/DeleteIcon";
import { datePickerFormItemProps, datePickerStandardProps } from "@/utils/componentUtils";
import { setErrorsToForm } from "@/utils/formUtils";
import { dateToIsoDateString } from "@/utils/utils";
import { validations } from "@/utils/validationUtils";
import { Col, DatePicker, Empty, Form, Modal, Row } from "antd";
import dayjs, { Dayjs } from "dayjs";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  useGetAgentCommissionsSettingsRankIntervals,
  useUpdateAgentCommissionsSettingsRankIntervals,
} from "../../../../../common/api/queries";
import HiddenInput from "../../../../../common/components/form/components/HiddenInput";
import { ModalSizes, rowGutter } from "../../../../../common/constants";
import { CreateUpdateEntries } from "../../../../../common/types";
import { UUID } from "../../../../../typings/global";
import { CommissionsSettingsRankSelect } from "../../../../enumerations/components/form/CommissionsSettingsRankSelect";
import { CreateUpdateCommissionsSettingsRankInterval } from "../../types";

type Props = {
  isOpen: boolean;
  onFormCancel: () => void;
  agentId: UUID;
};

export const CommissionsSettingsAssignRankModalForm = ({ isOpen, onFormCancel, agentId }: Props) => {
  const { t } = useTranslation();

  const [form] = Form.useForm<CreateUpdateEntries<CreateUpdateCommissionsSettingsRankInterval>>();
  const entries = Form.useWatch("entries", form);

  const { data: rankIntervals, isFetching } = useGetAgentCommissionsSettingsRankIntervals(agentId);
  const updateAgentCommissionsSettingsRankIntervals = useUpdateAgentCommissionsSettingsRankIntervals();

  useEffect(() => {
    if (isOpen && rankIntervals?.length) {
      form.setFieldsValue({
        entries: rankIntervals?.map(({ createdAt, updatedAt, rank, ...data }) => ({ ...data, rankId: rank.id })),
      });
    }
  }, [form, isOpen, rankIntervals]);

  const handleEndDateChange = (endDate: Dayjs, index: number) => {
    const entries = [...form.getFieldValue("entries")] as CreateUpdateCommissionsSettingsRankInterval[];

    if (entries.length > index + 1) {
      entries[index + 1] = {
        ...entries[index + 1],
        startDate: dateToIsoDateString(dayjs(endDate).add(1, "days")),
      } as CreateUpdateCommissionsSettingsRankInterval;

      form.setFieldsValue({ entries });
    }
  };

  const handleFormSubmit = () => {
    form.validateFields().then((values) =>
      updateAgentCommissionsSettingsRankIntervals.mutate(
        {
          id: agentId,
          object: values,
        },
        {
          onSuccess: onFormCancel,
          onError: (error) => setErrorsToForm(form, "commissions.rank.attrs.intervals", error.violations),
        },
      ),
    );
  };

  return (
    <Modal
      width={ModalSizes.MEDIUM}
      open={isOpen}
      loading={isFetching}
      title={t("commissions.settings.titles.agentRankAssign")}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={updateAgentCommissionsSettingsRankIntervals.isPending}
      afterClose={() => form.resetFields()}
      onOk={handleFormSubmit}
      onCancel={onFormCancel}
      mask={{ closable: false }}
    >
      <Form form={form} layout="vertical" className="margin-top-medium" name="commissionsSettingsAssignRankForm">
        <Form.List name="entries" initialValue={[{}]}>
          {(fields, { add, remove }) => (
            <>
              <Row gutter={rowGutter}>
                {fields.map(({ key, ...field }) => (
                  <React.Fragment key={key}>
                    <HiddenInput name={[field.name, "id"]} />
                    <HiddenInput name={[field.name, "optimisticLockVersion"]} />

                    <Col span={8}>
                      <Form.Item
                        name={[field.name, "rankId"]}
                        label={t("commissions.rank.attrs.intervals.rankId")}
                        rules={[validations.notNull]}
                      >
                        <CommissionsSettingsRankSelect />
                      </Form.Item>
                    </Col>

                    <Col span={7}>
                      <Form.Item
                        {...field}
                        name={[field.name, "startDate"]}
                        label={t("commissions.rank.attrs.intervals.startDate")}
                        rules={[validations.notNull]}
                        {...datePickerFormItemProps}
                      >
                        <DatePicker
                          {...datePickerStandardProps}
                          minDate={
                            fields.length > 1 && entries?.[field.name - 1]?.endDate
                              ? dayjs(entries?.[field.name - 1]?.endDate).add(1, "day")
                              : undefined
                          }
                        />
                      </Form.Item>
                    </Col>

                    <Col span={7}>
                      <Form.Item
                        {...field}
                        name={[field.name, "endDate"]}
                        label={t("commissions.rank.attrs.intervals.endDate")}
                        rules={[
                          field.name === fields.length - 1 ? validations.none : validations.notNull,
                          validations.notBefore(
                            entries?.[field.name]?.startDate,
                            t("commissions.rank.attrs.intervals.startDate"),
                          ),
                        ]}
                        {...datePickerFormItemProps}
                      >
                        <DatePicker
                          {...datePickerStandardProps}
                          defaultPickerValue={dayjs(entries?.[field.name]?.endDate ?? entries?.[field.name]?.startDate)}
                          minDate={
                            entries?.[field.name]?.startDate ? dayjs(entries?.[field.name]?.startDate) : undefined
                          }
                          allowClear={!(fields.length > 1 && field.name < fields.length - 1)}
                          onChange={(value) => (value ? handleEndDateChange(value, field.name) : undefined)}
                        />
                      </Form.Item>
                    </Col>

                    <Col span={2}>
                      <DeleteIcon onClick={() => remove(field.name)} />
                    </Col>
                  </React.Fragment>
                ))}
              </Row>

              {fields.length === 0 && <Empty />}

              <ActionButton
                icon="plus"
                onClick={() =>
                  add(
                    fields.length > 0
                      ? {
                          startDate: entries?.[fields.length - 1]?.endDate
                            ? dateToIsoDateString(dayjs(entries?.[fields.length - 1]?.endDate).add(1, "day"))
                            : undefined,
                        }
                      : {},
                  )
                }
              >
                {t("commissions.settings.actions.addInterval")}
              </ActionButton>
            </>
          )}
        </Form.List>
      </Form>
    </Modal>
  );
};
